// @flow

import * as React from 'react';
import {initialIntegrationCheckersData} from './data/initialData';
import type {IntegrationCheckersDataIntegrationCheckerResult} from '../generatedTypes';
import IntegrationCheckerRunner from './data/IntegrationCheckerRunner';
import type {GlobalsProps} from './index';

type Error = {
  code?: String,
  message?: String,
};
type IntegrationCheckerDataFetcherState = {
  runCheckerResult: IntegrationCheckersDataIntegrationCheckerResult,
  loadingCheckers: boolean,
  error: ?Error,
};

type IntegrationCheckerDataFetcherProps = {
  children: React.ComponentType<any>,
  runner: IntegrationCheckerRunner,
  startPolling?: boolean,
  loadOnInit?: boolean,
  globals: GlobalsProps,
};

class IntegrationCheckerDataFetcher extends React.Component<
  IntegrationCheckerDataFetcherProps,
  IntegrationCheckerDataFetcherState,
> {
  static defaultProps = {
    loadOnInit: false,
    startPolling: false,
  };

  constructor(props: IntegrationCheckerDataFetcherProps) {
    super(props);
    const {runner, startPolling, loadOnInit, globals} = props;

    runner.onData((data) => this.updateDataState(data));
    runner.onError((error) => this.updateErrorState(error));

    if (loadOnInit) {
      runner.runIntegrationChecker(globals.integrationCheck);
    }

    if (startPolling) {
      runner.startPolling(globals.integrationCheck);
    }
  }

  state = {
    runCheckerResult: initialIntegrationCheckersData,
    loadingCheckers: false,
    error: null,
  };

  updateDataState(data: IntegrationCheckersDataIntegrationCheckerResult) {
    const {runner} = this.props;
    this.setState({
      runCheckerResult: data,
      loadingCheckers: !!runner.isPolling,
    });
  }

  updateErrorState(error: ?Error) {
    const {runner} = this.props;
    this.setState({
      error,
      loadingCheckers: !!runner.isPolling,
    });
    if (
      window.siteAnalytics &&
      window.siteAnalytics.trackIntegrationCheckerAction
    ) {
      window.siteAnalytics.trackIntegrationCheckerAction(
        'run_tests_api_error',
        {
          error: error && error.message,
          code: error && error.code,
          guide: this.props.globals.integrationCheck,
        },
      );
    }
  }

  runCheckers(path: string, maxAttempts: number = Infinity) {
    const {runner} = this.props;
    this.setState({loadingCheckers: true});
    runner.startPolling(path, maxAttempts);
  }

  render() {
    const {children: ChildComponent} = this.props;
    const {runCheckerResult, loadingCheckers, error} = this.state;
    return (
      <ChildComponent
        runCheckerResult={runCheckerResult}
        loadingCheckers={loadingCheckers}
        runCheckers={(path, maxAttempts) => this.runCheckers(path, maxAttempts)}
        error={error}
      />
    );
  }
}

export default IntegrationCheckerDataFetcher;
